import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Eye, Edit, GripVertical, Maximize2, RectangleHorizontal, Square, SquareStack,
  Save, X, Plus, Link, Palette, Type, Image, Trash2, ArrowLeft, Monitor, Upload
} from "lucide-react";

// Size classes for grid items
const SIZE_CLASSES = {
  full: "col-span-12",
  half: "col-span-12 md:col-span-6",
  third: "col-span-12 md:col-span-4",
};
const SIZE_LABELS = { full: "Completo", half: "Medio", third: "Tercio" };
const SIZE_ICONS = { full: Maximize2, half: RectangleHorizontal, third: Square };

export default function TabLayoutEditor({ tabId, items, renderItem, onBack }) {
  const { getAuthHeaders } = useAuth();
  const [isPreview, setIsPreview] = useState(false);
  const [layoutConfig, setLayoutConfig] = useState({ header: null, footer: null, item_order: [], item_sizes: {}, title_style: {} });
  const [showHeaderEditor, setShowHeaderEditor] = useState(false);
  const [showFooterEditor, setShowFooterEditor] = useState(false);
  const [showTitleStyleEditor, setShowTitleStyleEditor] = useState(false);
  const [titleStyleForm, setTitleStyleForm] = useState({ font_family: "", color: "", font_size: "" });
  const [headerForm, setHeaderForm] = useState({ titulo: "", subtitulo: "", bg_color: "#0d9488", text_color: "#ffffff", logo_url: "", image_url: "", height: 160, buttons: [] });
  const [footerForm, setFooterForm] = useState({ texto: "", bg_color: "#1e293b", text_color: "#94a3b8", image_url: "", height: 100, buttons: [] });
  const [dragItem, setDragItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const dragOverItem = useRef(null);

  // Ordered items based on layout config
  const orderedItems = (() => {
    if (!layoutConfig.item_order?.length) return items;
    const orderMap = {};
    layoutConfig.item_order.forEach((id, idx) => { orderMap[id] = idx; });
    const sorted = [...items].sort((a, b) => {
      const aIdx = orderMap[a.id] !== undefined ? orderMap[a.id] : 9999;
      const bIdx = orderMap[b.id] !== undefined ? orderMap[b.id] : 9999;
      return aIdx - bIdx;
    });
    return sorted;
  })();

  const fetchLayout = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/informaciones/layout/${tabId}`, getAuthHeaders());
      setLayoutConfig(res.data);
      if (res.data.header) setHeaderForm(res.data.header);
      if (res.data.footer) setFooterForm(res.data.footer);
    } catch (err) { console.error(err); }
  }, [tabId, getAuthHeaders]);

  useEffect(() => { fetchLayout(); }, [fetchLayout]);

  const saveLayout = async (config) => {
    setSaving(true);
    try {
      await axios.put(`${API}/informaciones/layout/${tabId}`, { tab_id: tabId, ...config }, getAuthHeaders());
      toast.success("Layout guardado");
    } catch (err) { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleDragStart = (idx) => { setDragItem(idx); };
  const handleDragEnter = (idx) => { dragOverItem.current = idx; };
  const handleDragEnd = () => {
    if (dragItem === null || dragOverItem.current === null || dragItem === dragOverItem.current) {
      setDragItem(null);
      return;
    }
    const reordered = [...orderedItems];
    const [removed] = reordered.splice(dragItem, 1);
    reordered.splice(dragOverItem.current, 0, removed);
    const newOrder = reordered.map(i => i.id);
    const newConfig = { ...layoutConfig, item_order: newOrder };
    setLayoutConfig(newConfig);
    saveLayout(newConfig);
    setDragItem(null);
    dragOverItem.current = null;
  };

  const changeSize = (itemId, size) => {
    const newSizes = { ...layoutConfig.item_sizes, [itemId]: size };
    const newConfig = { ...layoutConfig, item_sizes: newSizes };
    setLayoutConfig(newConfig);
    saveLayout(newConfig);
  };

  const getSize = (itemId) => layoutConfig.item_sizes?.[itemId] || "full";

  const saveHeader = () => {
    const newConfig = { ...layoutConfig, header: headerForm };
    setLayoutConfig(newConfig);
    saveLayout(newConfig);
    setShowHeaderEditor(false);
    toast.success("Header guardado");
  };

  const saveFooter = () => {
    const newConfig = { ...layoutConfig, footer: footerForm };
    setLayoutConfig(newConfig);
    saveLayout(newConfig);
    setShowFooterEditor(false);
    toast.success("Footer guardado");
  };

  const removeHeader = () => {
    const newConfig = { ...layoutConfig, header: null };
    setLayoutConfig(newConfig);
    saveLayout(newConfig);
    setHeaderForm({ titulo: "", subtitulo: "", bg_color: "#0d9488", text_color: "#ffffff", logo_url: "", image_url: "", height: 160, buttons: [] });
  };

  const removeFooter = () => {
    const newConfig = { ...layoutConfig, footer: null };
    setLayoutConfig(newConfig);
    saveLayout(newConfig);
    setFooterForm({ texto: "", bg_color: "#1e293b", text_color: "#94a3b8", image_url: "", height: 100, buttons: [] });
  };

  const saveTitleStyle = () => {
    const newConfig = { ...layoutConfig, title_style: titleStyleForm };
    setLayoutConfig(newConfig);
    saveLayout(newConfig);
    setShowTitleStyleEditor(false);
    toast.success("Estilo de títulos guardado");
  };

  const uploadLayoutImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/informaciones/layout/${tabId}/upload-image`, fd, {
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
      });
      return res.data.url;
    } catch { toast.error("Error al subir imagen"); return null; }
  };

  // ========== HEADER RENDER ==========
  const HeaderBlock = ({ data, editable }) => {
    if (!data?.titulo && !data?.image_url) return null;
    const h = data.height || 160;
    return (
      <div className="rounded-xl overflow-hidden mb-6 shadow-lg relative" style={{ backgroundColor: data.bg_color || "#0d9488", minHeight: `${h}px` }}>
        {data.image_url && (
          <img src={data.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ minHeight: `${h}px` }} />
        )}
        <div className="relative px-8 flex items-center gap-6" style={{ color: data.text_color || "#fff", minHeight: `${h}px`, backgroundColor: data.image_url ? "rgba(0,0,0,0.3)" : "transparent" }}>
          {data.logo_url && <img src={data.logo_url} alt="Logo" className="w-16 h-16 rounded-xl object-cover bg-white/20 p-1" />}
          <div className="flex-1">
            {data.titulo && <h1 className="text-3xl font-bold">{data.titulo}</h1>}
            {data.subtitulo && <p className="text-lg opacity-80 mt-1">{data.subtitulo}</p>}
          </div>
          {data.buttons?.length > 0 && (
            <div className="flex gap-2">
              {data.buttons.map((btn, i) => (
                <a key={i} href={isPreview ? btn.url : "#"} target="_blank" rel="noreferrer" onClick={e => !isPreview && e.preventDefault()}>
                  <button className="px-5 py-2.5 rounded-lg font-medium text-sm transition-all hover:scale-105" style={{ backgroundColor: "rgba(255,255,255,0.2)", color: data.text_color, border: "1px solid rgba(255,255,255,0.3)" }}>
                    {btn.label}
                  </button>
                </a>
              ))}
            </div>
          )}
          {editable && (
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10" onClick={() => { setHeaderForm({ ...data, height: data.height || 160 }); setShowHeaderEditor(true); }}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-red-300 hover:bg-white/10" onClick={removeHeader}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========== FOOTER RENDER ==========
  const FooterBlock = ({ data, editable }) => {
    if (!data?.texto && !data?.buttons?.length && !data?.image_url) return null;
    const h = data.height || 100;
    return (
      <div className="rounded-xl overflow-hidden mt-6 shadow-lg relative" style={{ backgroundColor: data.bg_color || "#1e293b", minHeight: `${h}px` }}>
        {data.image_url && (
          <img src={data.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ minHeight: `${h}px` }} />
        )}
        <div className="relative px-8 flex items-center justify-between" style={{ color: data.text_color || "#94a3b8", minHeight: `${h}px`, backgroundColor: data.image_url ? "rgba(0,0,0,0.3)" : "transparent" }}>
          <p className="text-sm">{data.texto}</p>
          {data.buttons?.length > 0 && (
            <div className="flex gap-2">
              {data.buttons.map((btn, i) => (
                <a key={i} href={isPreview ? btn.url : "#"} target="_blank" rel="noreferrer" onClick={e => !isPreview && e.preventDefault()}>
                  <button className="px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80" style={{ backgroundColor: "rgba(255,255,255,0.1)", color: data.text_color, border: "1px solid rgba(255,255,255,0.15)" }}>
                    {btn.label}
                  </button>
                </a>
              ))}
            </div>
          )}
          {editable && (
            <div className="flex gap-1 ml-3">
              <Button variant="ghost" size="sm" className="opacity-60 hover:opacity-100" style={{ color: data.text_color }} onClick={() => { setFooterForm({ ...data, height: data.height || 100 }); setShowFooterEditor(true); }}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" className="opacity-60 hover:opacity-100 hover:text-red-400" style={{ color: data.text_color }} onClick={removeFooter}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========== BUTTONS EDITOR (reusable for header/footer) ==========
  const ButtonsEditor = ({ buttons, onChange }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm">Botones con Enlace</Label>
        <Button variant="outline" size="sm" onClick={() => onChange([...buttons, { label: "Nuevo Botón", url: "#" }])}>
          <Plus className="w-3 h-3 mr-1" /> Agregar
        </Button>
      </div>
      {buttons.map((btn, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input placeholder="Texto" value={btn.label} onChange={e => { const nb = [...buttons]; nb[i] = { ...nb[i], label: e.target.value }; onChange(nb); }} className="flex-1" />
          <Input placeholder="URL" value={btn.url} onChange={e => { const nb = [...buttons]; nb[i] = { ...nb[i], url: e.target.value }; onChange(nb); }} className="flex-1" />
          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => onChange(buttons.filter((_, idx) => idx !== i))}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ))}
    </div>
  );

  // ========== PREVIEW MODE ==========
  if (isPreview) {
    return (
      <div className="min-h-[80vh]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-700 gap-1"><Monitor className="w-3 h-3" /> Vista Previa del Usuario</Badge>
          </div>
          <Button onClick={() => setIsPreview(false)} variant="outline"><ArrowLeft className="w-4 h-4 mr-2" /> Volver al Editor</Button>
        </div>
        <HeaderBlock data={layoutConfig.header} editable={false} />
        <div className="grid grid-cols-12 gap-4">
          {orderedItems.map(item => (
            <div key={item.id} className={`${SIZE_CLASSES[getSize(item.id)]} transition-all`}>
              {renderItem(item, true)}
            </div>
          ))}
        </div>
        <FooterBlock data={layoutConfig.footer} editable={false} />
      </div>
    );
  }

  // ========== EDITOR MODE ==========
  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
        <div className="flex items-center gap-2">
          {onBack && <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4" /></Button>}
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Editor de Layout</span>
          <Badge variant="outline" className="text-xs">{orderedItems.length} elementos</Badge>
        </div>
        <div className="flex items-center gap-2">
          {!layoutConfig.header?.titulo && (
            <Button variant="outline" size="sm" onClick={() => setShowHeaderEditor(true)} data-testid="add-header-btn">
              <Type className="w-4 h-4 mr-1" /> Header
            </Button>
          )}
          {!layoutConfig.footer?.texto && !layoutConfig.footer?.buttons?.length && !layoutConfig.footer?.image_url && (
            <Button variant="outline" size="sm" onClick={() => setShowFooterEditor(true)} data-testid="add-footer-btn">
              <SquareStack className="w-4 h-4 mr-1" /> Footer
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => { setTitleStyleForm(layoutConfig.title_style || { font_family: "", color: "", font_size: "" }); setShowTitleStyleEditor(true); }} data-testid="title-style-btn">
            <Palette className="w-4 h-4 mr-1" /> Estilo Títulos
          </Button>
          <Button onClick={() => setIsPreview(true)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="preview-btn">
            <Eye className="w-4 h-4 mr-2" /> Vista Previa
          </Button>
        </div>
      </div>

      {/* Header */}
      <HeaderBlock data={layoutConfig.header} editable={true} />

      {/* Items Grid */}
      <div className="grid grid-cols-12 gap-4">
        {orderedItems.map((item, idx) => {
          const size = getSize(item.id);
          const SizeIcon = SIZE_ICONS[size];
          return (
            <div
              key={item.id}
              className={`${SIZE_CLASSES[size]} transition-all ${dragItem === idx ? "opacity-40" : ""}`}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragEnter={() => handleDragEnter(idx)}
              onDragEnd={handleDragEnd}
              onDragOver={e => e.preventDefault()}
            >
              <div className="relative group border-2 border-dashed border-transparent hover:border-teal-300 dark:hover:border-teal-700 rounded-xl transition-colors">
                {/* Drag handle + size controls */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 bg-white dark:bg-slate-800 rounded-full shadow-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity border">
                  <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
                  <div className="w-px h-4 bg-slate-200 mx-1" />
                  {["full", "half", "third"].map(s => {
                    const Icon = SIZE_ICONS[s];
                    return (
                      <button
                        key={s}
                        onClick={() => changeSize(item.id, s)}
                        className={`p-1 rounded transition-colors ${size === s ? "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300" : "text-slate-400 hover:text-slate-600"}`}
                        title={SIZE_LABELS[s]}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </button>
                    );
                  })}
                </div>
                {renderItem(item, false)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <FooterBlock data={layoutConfig.footer} editable={true} />

      {/* Header Editor Dialog */}
      <Dialog open={showHeaderEditor} onOpenChange={setShowHeaderEditor}>
        <DialogContent className="max-w-lg dark:bg-slate-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Configurar Header</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Título</Label><Input value={headerForm.titulo} onChange={e => setHeaderForm({...headerForm, titulo: e.target.value})} data-testid="header-titulo" /></div>
            <div><Label>Subtítulo</Label><Input value={headerForm.subtitulo} onChange={e => setHeaderForm({...headerForm, subtitulo: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Color de Fondo</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={headerForm.bg_color} onChange={e => setHeaderForm({...headerForm, bg_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={headerForm.bg_color} onChange={e => setHeaderForm({...headerForm, bg_color: e.target.value})} className="flex-1" />
                </div>
              </div>
              <div>
                <Label>Color de Texto</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={headerForm.text_color} onChange={e => setHeaderForm({...headerForm, text_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={headerForm.text_color} onChange={e => setHeaderForm({...headerForm, text_color: e.target.value})} className="flex-1" />
                </div>
              </div>
            </div>
            <div><Label>URL del Logo (opcional)</Label><Input value={headerForm.logo_url} onChange={e => setHeaderForm({...headerForm, logo_url: e.target.value})} placeholder="https://..." /></div>
            <div>
              <Label>Imagen de Fondo</Label>
              <div className="flex gap-2 items-center">
                <label className="flex-1 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-teal-400 transition-colors text-sm text-slate-500" data-testid="header-image-upload">
                  <Upload className="w-4 h-4 inline mr-1" />
                  {headerForm.image_url ? "Cambiar imagen" : "Subir imagen"}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const url = await uploadLayoutImage(file);
                    if (url) setHeaderForm({...headerForm, image_url: url});
                  }} />
                </label>
                {headerForm.image_url && (
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setHeaderForm({...headerForm, image_url: ""})}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {headerForm.image_url && <img src={headerForm.image_url} alt="" className="mt-2 rounded-lg h-16 object-cover w-full" />}
            </div>
            <div>
              <Label>Altura: {headerForm.height || 160}px</Label>
              <input type="range" min="80" max="400" step="10" value={headerForm.height || 160} onChange={e => setHeaderForm({...headerForm, height: parseInt(e.target.value)})} className="w-full mt-1" data-testid="header-height-slider" />
            </div>
            <ButtonsEditor buttons={headerForm.buttons || []} onChange={btns => setHeaderForm({...headerForm, buttons: btns})} />
            {/* Preview */}
            <div className="rounded-lg overflow-hidden relative" style={{ backgroundColor: headerForm.bg_color, minHeight: `${Math.min(headerForm.height || 160, 120)}px` }}>
              {headerForm.image_url && <img src={headerForm.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              <div className="relative px-6 py-4 flex items-center gap-4" style={{ color: headerForm.text_color, backgroundColor: headerForm.image_url ? "rgba(0,0,0,0.3)" : "transparent" }}>
                {headerForm.logo_url && <img src={headerForm.logo_url} alt="" className="w-10 h-10 rounded object-cover" />}
                <div><p className="font-bold">{headerForm.titulo || "Título"}</p>{headerForm.subtitulo && <p className="text-sm opacity-70">{headerForm.subtitulo}</p>}</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHeaderEditor(false)}>Cancelar</Button>
            <Button onClick={saveHeader} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-header-btn"><Save className="w-4 h-4 mr-2" /> Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer Editor Dialog */}
      <Dialog open={showFooterEditor} onOpenChange={setShowFooterEditor}>
        <DialogContent className="max-w-lg dark:bg-slate-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Configurar Footer</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Texto</Label><Input value={footerForm.texto} onChange={e => setFooterForm({...footerForm, texto: e.target.value})} data-testid="footer-texto" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Color de Fondo</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={footerForm.bg_color} onChange={e => setFooterForm({...footerForm, bg_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={footerForm.bg_color} onChange={e => setFooterForm({...footerForm, bg_color: e.target.value})} className="flex-1" />
                </div>
              </div>
              <div>
                <Label>Color de Texto</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={footerForm.text_color} onChange={e => setFooterForm({...footerForm, text_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={footerForm.text_color} onChange={e => setFooterForm({...footerForm, text_color: e.target.value})} className="flex-1" />
                </div>
              </div>
            </div>
            <div>
              <Label>Imagen de Fondo</Label>
              <div className="flex gap-2 items-center">
                <label className="flex-1 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-teal-400 transition-colors text-sm text-slate-500" data-testid="footer-image-upload">
                  <Upload className="w-4 h-4 inline mr-1" />
                  {footerForm.image_url ? "Cambiar imagen" : "Subir imagen"}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const url = await uploadLayoutImage(file);
                    if (url) setFooterForm({...footerForm, image_url: url});
                  }} />
                </label>
                {footerForm.image_url && (
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setFooterForm({...footerForm, image_url: ""})}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {footerForm.image_url && <img src={footerForm.image_url} alt="" className="mt-2 rounded-lg h-16 object-cover w-full" />}
            </div>
            <div>
              <Label>Altura: {footerForm.height || 100}px</Label>
              <input type="range" min="60" max="400" step="10" value={footerForm.height || 100} onChange={e => setFooterForm({...footerForm, height: parseInt(e.target.value)})} className="w-full mt-1" data-testid="footer-height-slider" />
            </div>
            <ButtonsEditor buttons={footerForm.buttons || []} onChange={btns => setFooterForm({...footerForm, buttons: btns})} />
            {/* Preview */}
            <div className="rounded-lg overflow-hidden relative" style={{ backgroundColor: footerForm.bg_color, minHeight: `${Math.min(footerForm.height || 100, 80)}px` }}>
              {footerForm.image_url && <img src={footerForm.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              <div className="relative px-6 py-3" style={{ color: footerForm.text_color, backgroundColor: footerForm.image_url ? "rgba(0,0,0,0.3)" : "transparent" }}>
                <p className="text-sm">{footerForm.texto || "Texto del footer..."}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFooterEditor(false)}>Cancelar</Button>
            <Button onClick={saveFooter} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-footer-btn"><Save className="w-4 h-4 mr-2" /> Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Title Style Editor Dialog */}
      <Dialog open={showTitleStyleEditor} onOpenChange={setShowTitleStyleEditor}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader><DialogTitle>Estilo de Títulos</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Personaliza el tipo y color de letra para los nombres de los elementos.</p>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Letra</Label>
              <select className="w-full border rounded-lg px-3 py-2 mt-1 dark:bg-slate-700 dark:border-slate-600" value={titleStyleForm.font_family || ""} onChange={e => setTitleStyleForm({...titleStyleForm, font_family: e.target.value})} data-testid="title-font-select">
                <option value="">Por defecto (Sistema)</option>
                <option value="Georgia, serif">Georgia (Serif)</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="'Playfair Display', serif">Playfair Display</option>
                <option value="'Merriweather', serif">Merriweather</option>
                <option value="'Lato', sans-serif">Lato</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="'Poppins', sans-serif">Poppins</option>
                <option value="'Open Sans', sans-serif">Open Sans</option>
                <option value="'Oswald', sans-serif">Oswald</option>
                <option value="'Raleway', sans-serif">Raleway</option>
                <option value="'Nunito', sans-serif">Nunito</option>
                <option value="'Bebas Neue', cursive">Bebas Neue</option>
                <option value="'Dancing Script', cursive">Dancing Script</option>
              </select>
            </div>
            <div>
              <Label>Color de Letra</Label>
              <div className="flex gap-2 items-center mt-1">
                <input type="color" value={titleStyleForm.color || "#0f172a"} onChange={e => setTitleStyleForm({...titleStyleForm, color: e.target.value})} className="w-10 h-10 rounded cursor-pointer" />
                <Input value={titleStyleForm.color || ""} onChange={e => setTitleStyleForm({...titleStyleForm, color: e.target.value})} placeholder="#0f172a" className="flex-1" />
                <Button variant="ghost" size="sm" onClick={() => setTitleStyleForm({...titleStyleForm, color: ""})} className="text-xs text-slate-400">Reset</Button>
              </div>
            </div>
            <div>
              <Label>Tamaño de Letra</Label>
              <select className="w-full border rounded-lg px-3 py-2 mt-1 dark:bg-slate-700 dark:border-slate-600" value={titleStyleForm.font_size || ""} onChange={e => setTitleStyleForm({...titleStyleForm, font_size: e.target.value})} data-testid="title-size-select">
                <option value="">Por defecto</option>
                <option value="0.75rem">Pequeño</option>
                <option value="0.875rem">Normal</option>
                <option value="1rem">Mediano</option>
                <option value="1.125rem">Grande</option>
                <option value="1.25rem">Extra Grande</option>
              </select>
            </div>
            {/* Preview */}
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-400 mb-2">Vista previa:</p>
              <p style={{ fontFamily: titleStyleForm.font_family || "inherit", color: titleStyleForm.color || "#0f172a", fontSize: titleStyleForm.font_size || "1rem" }} className="font-semibold">
                Ejemplo de Título de Álbum
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTitleStyleEditor(false)}>Cancelar</Button>
            <Button onClick={saveTitleStyle} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-title-style-btn"><Save className="w-4 h-4 mr-2" /> Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
